import React, { FC, ReactElement } from 'react';

import LinkElementView from '../../../editors/FormulaViewer/components/LinkElementView';
import AttributePlaceholderDataView from '../../../editors/PushNotificationEditor/Editor/components/SlateInput/AttributePlaceholder/AttributePlaceholderDataView';
import styled from 'styled-components';

//constants
import aampeTheme from '../../../constants/aampeTheme';

import { ElementKind } from '../../../models/editor.model';
//models
import {
    AlternateHeaderChildrenType,
    AlternateHeaderType,
} from '../../../models/formula.model';

import FormulaAlternateCmsDataView from './FormulaAlternateCmsDataView';
import FormulaAlternateDatasetView from './FormulaAlternateDatasetView';
//components
import FormulaAlternateVariantDataView from './FormulaAlternateVariantDataView';

interface FormulaAlternateHeaderDataViewProps {
    headerData: AlternateHeaderType;
    formulaId: number;
    isEditable: boolean;
    alternateCreateError: string | null;
    setAlternateCreateError: (value: string | null) => void;
}

const getChildComponent = (
    child: AlternateHeaderChildrenType,
    formulaId: number,
    isEditable: boolean,
    alternateCreateError: string | null,
    setAlternateCreateError: (value: string | null) => void,
): ReactElement => {
    if (child.kind === ElementKind.section) {
        return (
            <>
                {child.children?.map((child: AlternateHeaderChildrenType) => {
                    return getChildComponent(
                        child,
                        formulaId,
                        isEditable,
                        alternateCreateError,
                        setAlternateCreateError,
                    );
                })}
            </>
        );
    }
    return (
        <>
            {child.kind === ElementKind.variant ? (
                <FormulaAlternateVariantDataView
                    headerName={child.headerName}
                    childData={child}
                    formulaId={formulaId}
                    isEditable={isEditable}
                    alternateCreateError={alternateCreateError}
                    setAlternateCreateError={setAlternateCreateError}
                    selectedElement={{
                        kind: child.kind,
                        variantType: child.variantType,
                        elementId: child.id,
                    }}
                />
            ) : child.kind === ElementKind.dataset ? (
                <FormulaAlternateDatasetView data={child} />
            ) : child.kind === ElementKind.cms_placeholder ? (
                <FormulaAlternateCmsDataView
                    headerName={child.headerName}
                    cmsPlaceholderId={child.id}
                />
            ) : child.kind === ElementKind.link ? (
                <Content>
                    <RegularHeaderText>{child.headerName}</RegularHeaderText>
                    <LinkWrapper>
                        <LinkElementView element={child as any} />
                    </LinkWrapper>
                </Content>
            ) : child.kind === ElementKind.attributePlaceholder ? (
                <AttributePlaceholderDataView
                    headerName={child.headerName}
                    defaultValue={child.defaultValue}
                    attributeName={child.attributeName}
                />
            ) : (
                child.text !== '' && (
                    <Content>
                        <RegularHeaderText>
                            {child.headerName}
                        </RegularHeaderText>
                        <DataText>{child.text}</DataText>
                    </Content>
                )
            )}
        </>
    );
};

const FormulaAlternateHeaderDataView: FC<FormulaAlternateHeaderDataViewProps> =
    ({
        headerData,
        formulaId,
        isEditable,
        alternateCreateError,
        setAlternateCreateError,
    }) => {
        return (
            <Container>
                {headerData.children.map(
                    (child: AlternateHeaderChildrenType) => {
                        return getChildComponent(
                            child,
                            formulaId,
                            isEditable,
                            alternateCreateError,
                            setAlternateCreateError,
                        );
                    },
                )}
            </Container>
        );
    };

const Container = styled.div({
    display: 'flex',
    height: '100%',
});
const Content = styled.div({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    borderLeft: `1px solid ${aampeTheme.colors.interactiveWG01}`,
});
const RegularHeaderText = styled.div({
    display: 'flex',
    padding: aampeTheme.spacing4(2, 4),
    fontSize: aampeTheme.size.font.textMedium,
    lineHeight: '24px',
    fontWeight: 500,
    border: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    borderTop: '0px',
    borderRight: '0px',
    color: aampeTheme.colors.textAccessibleWG01,
    width: 220,
    height: '88px',
    backgroundColor: aampeTheme.colors.backgroundWG02,
    borderLeft: '0px',
});
const DataText = styled.div({
    display: 'flex',
    alignItems: 'center',
    padding: aampeTheme.spacing4(2, 4),
    fontSize: aampeTheme.size.font.default,
    lineHeight: '24px',
    fontWeight: 400,
    border: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    borderTop: '0px',
    borderRight: '0px',
    color: aampeTheme.colors.neutral09,
    width: '220px',
    minHeight: '45px',
    borderLeft: '0px',
    wordBreak: 'break-word',
    overflow: 'hidden',
});

const LinkWrapper = styled.div({
    fontSize: aampeTheme.size.font.default,
    lineHeight: '24px',
    fontWeight: 400,
    color: aampeTheme.colors.neutral09,
    border: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    borderTop: '0px',
    padding: aampeTheme.spacing4(2, 4),
});

export default FormulaAlternateHeaderDataView;
